import { useMemo } from "react";
import type { ManufacturerCatalogItemRow } from "@/lib/salesOrgPricingClient";

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

export function OrgSettingsSectionManufacturerCatalogTable(props: {
  busy: boolean;
  title: string;
  items: ManufacturerCatalogItemRow[];
  search: string;
  onSearchChange: (q: string) => void;
  showOverridePick: boolean;
  onPickForOverride: (row: ManufacturerCatalogItemRow) => void;
}) {
  const { busy, title, items, search, onSearchChange, showOverridePick, onPickForOverride } = props;
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter((it) => {
      const hay = [it.product_line, it.external_product_id ?? "", String(it.width_ft), String(it.length_ft)]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, q]);

  if (!items.length && !search) {
    return (
      <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
        <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">{title}</div>
        <p className="text-xs text-gray-500">No manufacturer catalog rows yet (publish a manufacturer CSV for this org).</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">{title}</div>
      <p className="text-[11px] text-gray-500">
        Browse the resolved manufacturer MSRP lines. Use &quot;Prefill override&quot; to copy dimensions into the subscriber override form below—then set your
        override MSRP and save.
      </p>
      <input
        type="search"
        className="w-full max-w-md text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 dark:border-gray-700"
        placeholder="Filter by product line, product id, or size…"
        value={search}
        disabled={busy}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-1.5 pr-2">Line id</th>
              <th className="py-1.5 pr-2">Product id</th>
              <th className="py-1.5 pr-2">Product line</th>
              <th className="py-1.5 pr-2">W</th>
              <th className="py-1.5 pr-2">L</th>
              <th className="py-1.5 pr-2">Pkg</th>
              <th className="py-1.5 pr-2">Opt</th>
              <th className="py-1.5 pr-2">Mfr MSRP</th>
              <th className="py-1.5 pr-2">Cur</th>
              {showOverridePick ? <th className="py-1.5 pr-0">Override</th> : null}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700 align-middle">
                <td className="py-1.5 pr-2 font-mono text-[10px] text-gray-500 whitespace-nowrap" title={row.id}>
                  {shortId(row.id)}
                </td>
                <td className="py-1.5 pr-2 font-mono">{row.external_product_id ?? "—"}</td>
                <td className="py-1.5 pr-2 max-w-[14rem] truncate" title={row.product_line}>
                  {row.product_line}
                </td>
                <td className="py-1.5 pr-2">{row.width_ft}</td>
                <td className="py-1.5 pr-2">{row.length_ft}</td>
                <td className="py-1.5 pr-2 font-mono">{row.package_key ?? "—"}</td>
                <td className="py-1.5 pr-2 font-mono">{row.options_key ?? "—"}</td>
                <td className="py-1.5 pr-2">{row.msrp}</td>
                <td className="py-1.5 pr-2">{row.currency || "USD"}</td>
                {showOverridePick ? (
                  <td className="py-1.5 pr-0 whitespace-nowrap">
                    <button
                      type="button"
                      disabled={busy}
                      className="text-indigo-600 hover:underline"
                      onClick={() => onPickForOverride(row)}
                    >
                      Prefill override
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        {q && filtered.length === 0 ? <div className="p-2 text-xs text-gray-500">No rows match this filter.</div> : null}
      </div>
    </div>
  );
}
